import React, { useState } from "react"
import toast from "react-hot-toast"
import { Search, Loader2, Phone, Globe, MapPin, Download, Plus, ExternalLink, KeyRound } from "lucide-react"
import {
  hasApiKey,
  searchProspects,
  recordSearchUsage,
  getSearchUsageThisMonth,
  exportProspectsToExcel,
  MAX_RESULTS_PER_SEARCH,
  type Prospect,
} from "../../services/placesSearch"
import { leadsStorage } from "../../services/leadsStorage"
import { PROSPECT_CATEGORIES } from "../../types/lead"

interface ProspectFinderProps {
  onAdded: (count: number) => void
}

const SetupInstructions: React.FC = () => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8 max-w-2xl">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
        <KeyRound className="w-5 h-5 text-primary-600" />
      </div>
      <h2 className="text-lg font-bold text-gray-900">Connect Google Places to enable this</h2>
    </div>
    <p className="text-sm text-gray-500 mb-5">
      Prospect search runs on Google's official Places API. It needs your own API key — Google gives a
      generous free monthly search allowance, but you need a Google Cloud account with a billing card on
      file to generate one.
    </p>
    <ol className="space-y-3 text-sm text-gray-700 list-decimal list-inside">
      <li>
        Go to{" "}
        <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">console.cloud.google.com</span>{" "}
        and create (or select) a project.
      </li>
      <li>
        Under <strong>APIs &amp; Services → Library</strong>, enable <strong>"Places API (New)"</strong>.
      </li>
      <li>
        Under <strong>APIs &amp; Services → Credentials</strong>, create an API key.
      </li>
      <li>
        Restrict the key: <strong>Application restrictions → HTTP referrers</strong>, add your site's
        domain (and <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">localhost/*</span>{" "}
        while testing).
      </li>
      <li>
        <strong>API restrictions</strong> → limit the key to "Places API (New)" only.
      </li>
      <li>Add a billing account to the project (required even to stay within the free tier).</li>
      <li>
        Paste the key into <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">.env</span>{" "}
        as{" "}
        <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
          VITE_GOOGLE_PLACES_API_KEY=your-key
        </span>
        , then restart the dev server (or redeploy).
      </li>
    </ol>
  </div>
)

const ProspectFinder: React.FC<ProspectFinderProps> = ({ onAdded }) => {
  const [category, setCategory] = useState("")
  const [location, setLocation] = useState("")
  const [resultCount, setResultCount] = useState(20)
  const [results, setResults] = useState<Prospect[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [hasSearched, setHasSearched] = useState(false)
  const [usage, setUsage] = useState(() => getSearchUsageThisMonth())

  if (!hasApiKey()) {
    return <SetupInstructions />
  }

  const runSearch = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!category.trim()) {
      toast.error("Enter or pick what you're looking for")
      return
    }
    const query = location.trim() ? `${category.trim()} in ${location.trim()}` : category.trim()
    setLoading(true)
    setError("")
    try {
      const found = await searchProspects(query, resultCount)
      setResults(found)
      setSelected(new Set())
      setHasSearched(true)
      setUsage(recordSearchUsage())
      if (found.length === 0) {
        toast("No results found for that search", { icon: "🔍" })
      }
    } catch (err) {
      console.error("Prospect search failed:", err)
      setError("Search failed. Check that your API key is valid and Places API (New) is enabled.")
    } finally {
      setLoading(false)
    }
  }

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    setSelected((prev) => (prev.size === results.length ? new Set() : new Set(results.map((r) => r.id))))
  }

  const selectedProspects = results.filter((r) => selected.has(r.id))

  const handleExport = () => {
    const toExport = selectedProspects.length > 0 ? selectedProspects : results
    if (toExport.length === 0) {
      toast.error("No results to export")
      return
    }
    exportProspectsToExcel(toExport)
  }

  const handleAddToLeads = () => {
    const toAdd = selectedProspects.length > 0 ? selectedProspects : results
    if (toAdd.length === 0) {
      toast.error("No results to add")
      return
    }
    toAdd.forEach((p) => {
      leadsStorage.add({
        name: p.name,
        phone: p.phone,
        city: p.address,
        interest: "Bulk / Institutional Order",
        budget: "Not sure yet",
        timeline: "Just exploring",
        source: "prospect",
        notes: [p.website && `Website: ${p.website}`, p.mapsUrl && `Maps: ${p.mapsUrl}`]
          .filter(Boolean)
          .join("\n"),
      })
    })
    toast.success(`${toAdd.length} prospect${toAdd.length > 1 ? "s" : ""} added to leads`)
    onAdded(toAdd.length)
  }

  return (
    <div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 mb-4">
        <div className="flex flex-wrap gap-2 mb-3">
          {PROSPECT_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                category === c
                  ? "bg-primary-600 text-white border-primary-600"
                  : "bg-white text-gray-600 border-gray-300 hover:border-primary-400"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <form onSubmit={runSearch} className="flex flex-col sm:flex-row gap-3">
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="What are you looking for? e.g. Interior designers"
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Where? e.g. Virar East, Thane"
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <select
            value={resultCount}
            onChange={(e) => setResultCount(Number(e.target.value))}
            title="Number of results"
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-700"
          >
            {[5, 10, 15, 20].map((n) => (
              <option key={n} value={n}>
                {n} results
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center justify-center gap-2 bg-primary-600 text-white px-5 py-2.5 rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Search
          </button>
        </form>
        <p className="text-xs text-gray-400 mt-3">
          {usage} search{usage === 1 ? "" : "es"} this month · {MAX_RESULTS_PER_SEARCH} results is Google's
          per-search maximum · free tier covers roughly 5,000 searches/month
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {results.length > 0 && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={selected.size === results.length && results.length > 0}
                onChange={toggleSelectAll}
                className="rounded border-gray-300"
              />
              {selected.size > 0 ? `${selected.size} selected` : `${results.length} results`}
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExport}
                className="flex items-center gap-2 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                <Download className="w-4 h-4" /> Download Excel
              </button>
              <button
                onClick={handleAddToLeads}
                className="flex items-center gap-2 bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 text-sm font-medium"
              >
                <Plus className="w-4 h-4" /> Add to Leads
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-4 py-3 w-8"></th>
                    <th className="text-left px-4 py-3 font-medium">Business</th>
                    <th className="text-left px-4 py-3 font-medium">Contact</th>
                    <th className="text-left px-4 py-3 font-medium">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.map((r) => (
                    <tr key={r.id} className={`hover:bg-gray-50 ${selected.has(r.id) ? "bg-primary-50" : ""}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(r.id)}
                          onChange={() => toggleSelected(r.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{r.name}</div>
                        {r.address && (
                          <div className="text-xs text-gray-400 flex items-start gap-1 mt-0.5 max-w-xs">
                            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" /> {r.address}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {r.phone && (
                          <div className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                            <Phone className="w-3.5 h-3.5 text-primary-500" /> {r.phone}
                          </div>
                        )}
                        {r.website && (
                          <a
                            href={r.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-primary-600 hover:underline mt-1"
                          >
                            <Globe className="w-3.5 h-3.5" /> Website <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap capitalize">
                        {r.primaryType.replace(/_/g, " ") || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && hasSearched && results.length === 0 && !error && (
        <div className="text-center py-16 px-4 bg-white rounded-xl border border-gray-100">
          <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No results</p>
          <p className="text-gray-400 text-sm mt-1">Try a broader area or a different category.</p>
        </div>
      )}
    </div>
  )
}

export default ProspectFinder
